import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { getEvents, createBudget, getCategories, createCategory } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, ChevronDown, ChevronUp } from 'lucide-react';

const CreateBudget = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    event_id: '',
    notes: '',
  });
  const [categories, setCategories] = useState([
    { 
      name: '', 
      expanded: true,
      items: [{ description: '', amount: '' }]
    }
  ]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [eventsRes, categoriesRes] = await Promise.all([
        getEvents(),
        getCategories()
      ]);
      setEvents(eventsRes.data.filter(e => e.status === 'active'));
      setAvailableCategories(categoriesRes.data.categories || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    try {
      await createCategory({ name: newCategoryName.trim() });
      toast.success('Category created!');
      
      // Refresh categories
      const categoriesRes = await getCategories();
      setAvailableCategories(categoriesRes.data.categories || []);
      setNewCategoryName('');
      setShowAddCategory(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const addCategory = () => {
    setCategories([...categories, { 
      name: '', 
      expanded: true,
      items: [{ description: '', amount: '' }]
    }]);
  };

  const removeCategory = (index) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const toggleCategory = (index) => {
    const updated = [...categories];
    updated[index].expanded = !updated[index].expanded;
    setCategories(updated);
  };

  const updateCategoryName = (index, name) => {
    const updated = [...categories];
    updated[index].name = name;
    setCategories(updated);
  };

  const addItem = (categoryIndex) => {
    const updated = [...categories];
    updated[categoryIndex].items.push({ description: '', amount: '' });
    setCategories(updated);
  };

  const removeItem = (categoryIndex, itemIndex) => {
    const updated = [...categories];
    updated[categoryIndex].items = updated[categoryIndex].items.filter((_, i) => i !== itemIndex);
    if (updated[categoryIndex].items.length === 0) {
      updated[categoryIndex].items = [{ description: '', amount: '' }];
    }
    setCategories(updated);
  };

  const updateItem = (categoryIndex, itemIndex, field, value) => {
    const updated = [...categories];
    updated[categoryIndex].items[itemIndex][field] = value;
    setCategories(updated);
  };

  const getCategoryTotal = (category) => {
    return category.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const getGrandTotal = () => {
    return categories.reduce((sum, cat) => sum + getCategoryTotal(cat), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_id) {
      toast.error('Please select an event');
      return;
    }
    
    // Validate categories
    const validCategories = categories
      .filter(cat => cat.name)
      .map(cat => ({
        name: cat.name,
        amount: getCategoryTotal(cat),
        items: cat.items
          .filter(item => item.description || item.amount)
          .map(item => ({
            description: item.description,
            amount: parseFloat(item.amount) || 0
          }))
      }));

    if (validCategories.length === 0) {
      toast.error('Please add at least one category');
      return;
    }

    const totalAmount = getGrandTotal();
    if (totalAmount <= 0) {
      toast.error('Please add amounts to your budget');
      return;
    }

    setLoading(true);
    try {
      await createBudget({
        event_id: formData.event_id,
        total_amount: totalAmount,
        categories: validCategories,
        notes: formData.notes,
      });
      toast.success('Budget submitted for approval!');
      navigate('/budgets');
    } catch (error) {
      toast.error('Failed to create budget');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/budgets')} size="sm">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Create Budget</h2>
              <p className="text-sm text-muted-foreground">Submit budget for approval</p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Event Selection */}
            <Card>
              <CardContent className="p-4">
                <Label htmlFor="event">Select Event *</Label>
                <Select value={formData.event_id} onValueChange={(value) => setFormData({ ...formData, event_id: value })}>
                  <SelectTrigger data-testid="event-select" className="mt-2">
                    <SelectValue placeholder="Choose an event" />
                  </SelectTrigger>
                  <SelectContent>
                    {events.map((event) => (
                      <SelectItem key={event.event_id} value={event.event_id}>
                        {event.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Categories with Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Budget Categories</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCategory} data-testid="add-category-button">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Category
                </Button>
              </div>

              {categories.map((category, catIndex) => (
                <Card key={catIndex} className="overflow-hidden">
                  {/* Category Header */}
                  <div 
                    className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer"
                    onClick={() => toggleCategory(catIndex)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Select 
                        value={category.name} 
                        onValueChange={(value) => {
                          if (value === '__add_new__') {
                            setShowAddCategory(true);
                          } else {
                            updateCategoryName(catIndex, value);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <SelectTrigger className="w-48 bg-white" data-testid={`category-select-${catIndex}`}>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCategories.map((cat) => (
                            <SelectItem key={cat.category_id} value={cat.name}>
                              {cat.name}
                              {!cat.is_default && <span className="ml-2 text-xs text-muted-foreground">(custom)</span>}
                            </SelectItem>
                          ))}
                          <SelectItem value="__add_new__" className="text-primary font-medium">
                            <span className="flex items-center gap-1">
                              <Plus className="h-3 w-3" /> Add New Category
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="font-semibold text-primary">
                        ₹{getCategoryTotal(category).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {categories.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); removeCategory(catIndex); }}
                          data-testid={`remove-category-${catIndex}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      {category.expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>

                  {/* Category Items */}
                  {category.expanded && (
                    <CardContent className="p-4 space-y-3">
                      <p className="text-sm text-muted-foreground mb-2">Add specific items under this category:</p>
                      
                      {category.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-center gap-2">
                          <Input
                            placeholder="Item description (e.g., Microphones, Speakers)"
                            value={item.description}
                            onChange={(e) => updateItem(catIndex, itemIndex, 'description', e.target.value)}
                            className="flex-1"
                            data-testid={`item-desc-${catIndex}-${itemIndex}`}
                          />
                          <div className="relative w-32">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                            <Input
                              type="number"
                              placeholder="Amount"
                              value={item.amount}
                              onChange={(e) => updateItem(catIndex, itemIndex, 'amount', e.target.value)}
                              className="pl-7"
                              data-testid={`item-amount-${catIndex}-${itemIndex}`}
                            />
                          </div>
                          {category.items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(catIndex, itemIndex)}
                              className="h-9 w-9"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => addItem(catIndex)}
                        className="text-primary"
                        data-testid={`add-item-${catIndex}`}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Item
                      </Button>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>

            {/* Total and Notes */}
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                  <span className="font-semibold text-lg">Grand Total</span>
                  <span className="font-bold text-2xl text-primary" data-testid="grand-total">
                    ₹{getGrandTotal().toLocaleString('en-IN')}
                  </span>
                </div>

                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="budget-notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional information about this budget"
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading} 
                    data-testid="submit-budget-button" 
                    className="flex-1"
                  >
                    {loading ? 'Submitting...' : 'Submit for Approval'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/budgets')}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>

        <MobileNav user={user} />
      </div>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              Add New Category
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Category Name</Label>
            <Input
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="e.g., Mobilization Event"
              className="mt-2"
              data-testid="new-category-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              This category will be available for all events.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCategory(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory} data-testid="create-category-btn">
              Create Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateBudget;
